import {
  Flag,
  Forward,
  Mail,
  MailOpen,
  Reply,
  ReplyAll,
  Star,
  Trash2,
} from "lucide-react";
import { MailIconTooltipButton } from "@/features/shared/MailIconTooltipButton";
import { MessageSenderMeta } from "@/features/message/MessageSenderMeta";
import { formatMailDetailDate, extractSenderLabel } from "@/lib/formatMailDate";
import { ContactAvatar } from "@/features/contacts/ContactAvatar";
import { cn } from "@/lib/cn";

type Props = {
  subject: string;
  from: string;
  to: string;
  cc?: string;
  date: string;
  seen?: boolean;
  flagged?: boolean;
  favorite?: boolean;
  /** false si le sujet est déjà affiché ailleurs (ex. header mobile). */
  showSubject?: boolean;
  /** Sélection multiple active — désactive répondre / transférer. */
  bulkSelectionActive?: boolean;
  /** Mobile : actions réparties, ou masquées (footer + menu ⋯). */
  actionsLayout?: "default" | "spread" | "hidden";
  onAction?: (actionId: string) => void;
};

type ActionBtn = {
  id: string;
  label: string;
  icon: typeof Reply;
  danger?: boolean;
  pressed?: boolean;
  activeClass?: string;
  disabled?: boolean;
};

export function MessageReaderHeader({
  subject,
  from,
  to,
  cc = "",
  date,
  seen = true,
  flagged = false,
  favorite = false,
  showSubject = true,
  bulkSelectionActive = false,
  actionsLayout = "default",
  onAction,
}: Props) {
  const dateLabel = formatMailDetailDate(date);
  const senderLabel = extractSenderLabel(from) || "Expéditeur inconnu";
  const spread = actionsLayout === "spread";
  const hideActions = actionsLayout === "hidden";

  function handleAction(actionId: string) {
    onAction?.(actionId);
  }

  const composeDisabled = bulkSelectionActive;

  const actions: ActionBtn[] = [
    { id: "reply", label: "Répondre", icon: Reply, disabled: composeDisabled },
    { id: "replyAll", label: "Répondre à tous", icon: ReplyAll, disabled: composeDisabled },
    { id: "forward", label: "Transférer", icon: Forward, disabled: composeDisabled },
    {
      id: "toggleImportant",
      label: flagged ? "Retirer important" : "Marquer important",
      icon: Flag,
      pressed: flagged,
      activeClass: "text-amber-500",
    },
    {
      id: "toggleFavorite",
      label: favorite ? "Retirer des favoris" : "Ajouter aux favoris",
      icon: Star,
      pressed: favorite,
      activeClass: "text-amber-400",
    },
    seen
      ? { id: "markUnread", label: "Marquer comme non lu", icon: Mail }
      : { id: "markRead", label: "Marquer comme lu", icon: MailOpen },
    { id: "delete", label: "Supprimer", icon: Trash2, danger: true },
  ];

  return (
    <div
      className={cn(
        "shrink-0 border-b border-[var(--mail-border-soft)] bg-[var(--mail-surface)]",
        hideActions ? "px-3 py-2.5" : spread ? "px-2 py-2.5" : "px-3 py-2"
      )}
    >
      {!hideActions ? (
      <div
        className={cn(
          "mb-1.5 flex items-center",
          spread ? "w-full gap-0" : "flex-wrap gap-0.5"
        )}
        role="toolbar"
        aria-label="Actions sur le message"
      >
        {actions.map(({ id, label, icon: Icon, danger, pressed, activeClass, disabled }) => (
          <MailIconTooltipButton
            key={id}
            label={label}
            side="bottom"
            onClick={() => handleAction(id)}
            disabled={!onAction || disabled}
            aria-pressed={pressed}
            tooltipClassName={spread ? "min-w-0 flex-1" : undefined}
            className={cn(
              "inline-flex items-center justify-center rounded-full transition",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--mail-accent)]",
              spread ? "h-11 w-full" : "h-7 w-7",
              danger
                ? "text-[var(--mail-danger-text)] hover:bg-[var(--mail-danger-soft)]"
                : pressed
                  ? cn(activeClass, "hover:bg-[var(--mail-surface-hover)]")
                  : "text-[var(--mail-muted)] hover:bg-[var(--mail-surface-hover)] hover:text-[var(--mail-text)]",
              (!onAction || disabled) && "cursor-not-allowed opacity-40"
            )}
          >
            <Icon
              className={cn(
                "shrink-0",
                spread ? "h-5 w-5" : "h-3.5 w-3.5",
                id === "toggleImportant" && pressed && "fill-amber-500",
                id === "toggleFavorite" && pressed && "fill-amber-400"
              )}
              aria-hidden
            />
          </MailIconTooltipButton>
        ))}
      </div>
      ) : null}

      <div className="flex items-start gap-2">
        <ContactAvatar label={senderLabel} fromRaw={from} size="sm" />

        <MessageSenderMeta from={from} dateLabel={dateLabel} />
      </div>

      {to.trim() || cc.trim() ? (
        <div className="mt-1 space-y-0.5 text-[11px] leading-snug text-[var(--mail-muted)]">
          {to.trim() ? (
            <p className="min-w-0 truncate" title={to}>
              <span className="font-medium text-[var(--mail-text)]/75">À</span> {to}
            </p>
          ) : null}
          {cc.trim() ? (
            <p className="min-w-0 truncate" title={cc}>
              <span className="font-medium text-[var(--mail-text)]/75">Cc</span> {cc}
            </p>
          ) : null}
        </div>
      ) : null}

      {showSubject ? (
        <h2
          className="mt-1 truncate text-sm font-medium leading-snug text-[var(--mail-text)]"
          title={subject}
        >
          {subject}
        </h2>
      ) : null}
    </div>
  );
}
